import React, { useState } from "react";
import axios from "axios";
import { Loader2, X, Plus, Trash2, AlertCircle } from "lucide-react";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import Swal from "sweetalert2";

const CreateBlogModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subDescriptions: [""],
    label: [{ image: null, text: "" }],
    quote: { title: "", text: "" },
    feature: {
      heading: "",
      description: "",
      image: null,
      subDescriptions: [""],
      features: [""],
    },
  });

  const [mainFile, setMainFile] = useState(null);
  const [featurePreview, setFeaturePreview] = useState(null);
  const [images, setImages] = useState({ mainImage: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubDescChange = (index, value) => {
    const updated = [...formData.subDescriptions];
    updated[index] = value;
    setFormData({ ...formData, subDescriptions: updated });
  };
  const addSubDesc = () =>
    setFormData({
      ...formData,
      subDescriptions: [...formData.subDescriptions, ""],
    });
  const removeSubDesc = (index) => {
    const updated = formData.subDescriptions.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      subDescriptions: updated.length ? updated : [""],
    });
  };

  const handleLabelTextChange = (index, value) => {
    const updated = [...formData.label];
    updated[index].text = value;
    setFormData({ ...formData, label: updated });
  };

  const handleLabelImageChange = (index, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const updated = [...formData.label];
      updated[index].image = file;
      updated[index].preview = reader.result;
      setFormData({ ...formData, label: updated });
    };
    reader.readAsDataURL(file);
  };

  const addLabel = () => {
    setFormData({
      ...formData,
      label: [...formData.label, { image: null, text: "" }],
    });
  };

  const removeLabel = (index) => {
    const updated = formData.label.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      label: updated.length ? updated : [{ image: null, text: "" }],
    });
  };

  const handleQuoteChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      quote: { ...prev.quote, [field]: value },
    }));
  };

  const handleFeatureChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      feature: { ...prev.feature, [field]: value },
    }));
  };

  const handleFeatureSubDescChange = (index, value) => {
    const updated = [...formData.feature.subDescriptions];
    updated[index] = value;
    setFormData({
      ...formData,
      feature: { ...formData.feature, subDescriptions: updated },
    });
  };
  const addFeatureSubDesc = () =>
    setFormData({
      ...formData,
      feature: {
        ...formData.feature,
        subDescriptions: [...formData.feature.subDescriptions, ""],
      },
    });
  const removeFeatureSubDesc = (index) => {
    const updated = formData.feature.subDescriptions.filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      feature: {
        ...formData.feature,
        subDescriptions: updated.length ? updated : [""],
      },
    });
  };

  const handleFeatureListChange = (index, value) => {
    const updated = [...formData.feature.features];
    updated[index] = value;
    setFormData({
      ...formData,
      feature: { ...formData.feature, features: updated },
    });
  };
  const addFeatureList = () =>
    setFormData({
      ...formData,
      feature: {
        ...formData.feature,
        features: [...formData.feature.features, ""],
      },
    });
  const removeFeatureList = (index) => {
    const updated = formData.feature.features.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      feature: {
        ...formData.feature,
        features: updated.length ? updated : [""],
      },
    });
  };

  const handleMainImage = (e) => {
    const f = e.target.files[0];
    setMainFile(f);
    const r = new FileReader();
    r.onloadend = () => setImages({ mainImage: r.result });
    r.readAsDataURL(f);
  };

  const handleFeatureImage = (e) => {
    const f = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      feature: { ...prev.feature, image: f },
    }));
    const r = new FileReader();
    r.onloadend = () => setFeaturePreview(r.result);
    r.readAsDataURL(f);
  };

  const validate = () => {
    if (!formData.title.trim()) return "Blog title is required";
    if (!formData.description.trim()) return "Blog description is required";
    if (!mainFile) return "Main image is required";
    if (
      !formData.label.length ||
      formData.label.some((l) => !l.image || !l.text.trim())
    )
      return "All label items (image & text) are required";
    if (!formData.quote.title.trim() || !formData.quote.text.trim())
      return "Quote title and text are required";
    if (
      !formData.feature.heading.trim() ||
      !formData.feature.description.trim() ||
      !formData.feature.image
    )
      return "Feature section is incomplete";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validate();
    if (validation) return setError(validation);

    setLoading(true);
    setError(null);

    try {
      const mainImgUrl = await uploadToCloudinary(mainFile);
      const featureImgUrl = await uploadToCloudinary(formData.feature.image);

      const labelUploads = await Promise.all(
        formData.label.map(async (l) => ({
          image: await uploadToCloudinary(l.image),
          text: l.text,
        }))
      );

      const submitData = {
        title: formData.title,
        description: formData.description,
        image: mainImgUrl,
        subDescriptions: formData.subDescriptions.filter(
          (i) => i.trim() !== ""
        ),
        label: labelUploads,
        quote: formData.quote,
        feature: {
          heading: formData.feature.heading,
          description: formData.feature.description,
          subDescriptions: formData.feature.subDescriptions.filter(
            (i) => i.trim() !== ""
          ),
          image: featureImgUrl,
          features: formData.feature.features.filter((i) => i.trim() !== ""),
        },
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/blog/create-blog`,
        submitData
      );

      if (res.data.success) {
        onSuccess();
        onClose();
        Swal.fire("Created", "Blog created successfully.", "success");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to create blog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-[#0bb6d9] to-[#3cd7f6] text-white p-6 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-lg font-semibold text-white">
            Create New Blog
          </h2>
          <button
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5" /> {error}
            </div>
          )}

          <div>
            <label className="block font-semibold text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter blog title"
              className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter blog description"
              className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-1">
              Sub Descriptions *
            </label>
            {formData.subDescriptions.map((desc, i) => (
              <div className="flex gap-2 mt-2" key={i}>
                <input
                  value={desc}
                  onChange={(e) => handleSubDescChange(i, e.target.value)}
                  className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                  placeholder={`Sub description ${i + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeSubDesc(i)}
                  className="text-red-500"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addSubDesc}
              className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
            >
              <Plus size={18} className="mr-1" /> Add Sub Description
            </button>
          </div>

          <div className="border rounded-xl p-4 space-y-4">
            <h4 className="font-semibold text-gray-800">Labels *</h4>
            {formData.label.map((lbl, i) => (
              <div
                key={i}
                className="flex flex-col md:flex-row gap-3 items-start md:items-center"
              >
                <div className="flex-1 w-full">
                  <input
                    type="text"
                    value={lbl.text}
                    onChange={(e) => handleLabelTextChange(i, e.target.value)}
                    placeholder="Label text"
                    className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                  />
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleLabelImageChange(i, e.target.files[0])
                    }
                    className="text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7]"
                  />
                  {lbl.preview && (
                    <img
                      src={lbl.preview}
                      alt="preview"
                      className="w-20 h-20 mt-2 rounded object-cover"
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeLabel(i)}
                  className="text-red-500"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addLabel}
              className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
            >
              <Plus size={18} className="mr-1" /> Add Label
            </button>
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-1">
              Quote *
            </label>
            <input
              type="text"
              placeholder="Enter quote title"
              value={formData.quote.title}
              onChange={(e) => handleQuoteChange("title", e.target.value)}
              className="w-full border rounded-lg p-2.5 mb-2 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
            />
            <textarea
              placeholder="Enter quote text"
              value={formData.quote.text}
              onChange={(e) => handleQuoteChange("text", e.target.value)}
              className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
              rows={2}
            />
          </div>

          <div className="border rounded-xl p-4 space-y-4">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Feature Heading *
              </label>
              <input
                type="text"
                placeholder="Feature Heading"
                value={formData.feature.heading}
                onChange={(e) => handleFeatureChange("heading", e.target.value)}
                className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Feature Description *
              </label>
              <textarea
                placeholder="Feature Description"
                value={formData.feature.description}
                onChange={(e) =>
                  handleFeatureChange("description", e.target.value)
                }
                className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                rows={2}
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Feature Sub Descriptions *
              </label>
              {formData.feature.subDescriptions.map((desc, i) => (
                <div className="flex gap-2 mt-2" key={i}>
                  <input
                    value={desc}
                    onChange={(e) =>
                      handleFeatureSubDescChange(i, e.target.value)
                    }
                    className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeFeatureSubDesc(i)}
                    className="text-red-500"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFeatureSubDesc}
                className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
              >
                <Plus size={18} className="mr-1" /> Add Sub Description
              </button>
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Features *
              </label>
              {formData.feature.features.map((f, i) => (
                <div className="flex gap-2 mt-2" key={i}>
                  <input
                    value={f}
                    onChange={(e) => handleFeatureListChange(i, e.target.value)}
                    className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-[#0BB6D9] outline-none"
                    placeholder={`Feature ${i + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeFeatureList(i)}
                    className="text-red-500"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFeatureList}
                className="flex items-center text-[#0BB6D9] mt-2 hover:underline"
              >
                <Plus size={18} className="mr-1" /> Add Feature
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Main Image *
              </label>
              <input
                type="file"
                onChange={handleMainImage}
                required
                className="w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7]"
              />
              {images.mainImage && (
                <img
                  src={images.mainImage}
                  className="w-28 h-28 mt-2 rounded object-cover"
                  alt="Main"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Feature Image *
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFeatureImage}
                required
                className="w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7]"
              />
              {featurePreview && (
                <img
                  src={featurePreview}
                  alt="preview"
                  className="w-28 h-28 mt-2 rounded object-cover"
                />
              )}
            </div>
          </div>

          <div className="flex items-center gap-5 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 w-full py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 w-full rounded-lg bg-[#0BB6D9] text-white hover:bg-cyan-700 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="animate-spin w-4 h-4" />}
              <span>Create Blog</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBlogModal;